import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const atLeastOne = (v) => Object.keys(v).length > 0;
/** Money is an integer count of the currency's minor unit (cents, paise, ...), never a decimal. */
export const MAX_MINOR = 1_000_000_000_000;
export const minorSchema = z.number().int().min(1).max(MAX_MINOR);
export const signedMinorSchema = z
    .number()
    .int()
    .min(-MAX_MINOR)
    .max(MAX_MINOR)
    .refine((v) => v !== 0, 'Amount cannot be zero');
// ---------------------------------------------------------------- fee heads
export const createFeeHeadSchema = z.object({
    name: text(100).min(1),
    incomeAccountId: uuidSchema.optional(),
});
export const updateFeeHeadSchema = z
    .object({ name: text(100).min(1), incomeAccountId: uuidSchema, isActive: z.boolean() })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
// ---------------------------------------------------------- fee structures
export const createFeeItemSchema = z.object({
    academicYearId: uuidSchema.optional(),
    classId: uuidSchema,
    feeHeadId: uuidSchema,
    amountMinor: minorSchema,
    dueDate: isoDate,
});
export const listFeeItemsQuerySchema = z.object({
    academicYearId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
});
// ---------------------------------------------------------------- invoices
export const generateInvoicesSchema = z
    .object({
    academicYearId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
    studentIds: z.array(uuidSchema).min(1).max(500).optional(),
    issueDate: isoDate.optional(),
})
    .refine((v) => !!v.classId || !!v.studentIds, {
    message: 'Choose a class or specific students',
    path: ['classId'],
});
export const INVOICE_FILTERS = ['outstanding', 'overdue', 'paid', 'cancelled'];
export const listInvoicesQuerySchema = paginationQuerySchema.extend({
    studentId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
    academicYearId: uuidSchema.optional(),
    filter: z.enum(INVOICE_FILTERS).optional(),
});
export const adjustInvoiceSchema = z.object({
    description: text(200).min(1),
    /** Negative = discount or concession, positive = extra charge. */
    amountMinor: signedMinorSchema,
});
export const cancelInvoiceSchema = z.object({ reason: text(300).min(1) });
// ---------------------------------------------------------------- payments
export const PAYMENT_METHODS = [
    'cash',
    'cheque',
    'bank_transfer',
    'card',
    'upi',
    'online',
];
export const recordPaymentSchema = z
    .object({
    studentId: uuidSchema,
    amountMinor: minorSchema,
    method: z.enum(PAYMENT_METHODS),
    reference: optionalText(100),
    paidOn: isoDate.optional(),
    /** Omit to pay the oldest dues first. */
    allocations: z
        .array(z.object({ invoiceId: uuidSchema, amountMinor: minorSchema }))
        .min(1)
        .max(50)
        .optional(),
})
    .refine((v) => !v.allocations ||
    new Set(v.allocations.map((a) => a.invoiceId)).size === v.allocations.length, {
    message: 'An invoice can appear only once',
    path: ['allocations'],
})
    .refine((v) => !v.allocations || v.allocations.reduce((sum, a) => sum + a.amountMinor, 0) === v.amountMinor, {
    message: 'Allocations must add up to the amount',
    path: ['allocations'],
});
export const voidPaymentSchema = z.object({ reason: text(300).min(1) });
export const listPaymentsQuerySchema = paginationQuerySchema.extend({
    studentId: uuidSchema.optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    method: z.enum(PAYMENT_METHODS).optional(),
    status: z.enum(['valid', 'void']).optional(),
});
// -------------------------------------------------------------- accounting
export const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];
export const createAccountSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[0-9A-Za-z][0-9A-Za-z._-]{0,19}$/, 'Code: 1-20 letters, digits, ".", "_" or "-"'),
    name: text(100).min(1),
    accountType: z.enum(ACCOUNT_TYPES),
});
export const updateAccountSchema = z
    .object({ name: text(100).min(1), isActive: z.boolean() })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const journalLineSchema = z
    .object({
    accountId: uuidSchema,
    debitMinor: z.number().int().min(0).max(MAX_MINOR).default(0),
    creditMinor: z.number().int().min(0).max(MAX_MINOR).default(0),
    memo: optionalText(200),
})
    .refine((v) => v.debitMinor > 0 !== v.creditMinor > 0, {
    message: 'Each line is either a debit or a credit',
    path: ['debitMinor'],
});
export const postJournalEntrySchema = z
    .object({
    entryDate: isoDate,
    memo: text(300).min(1),
    lines: z.array(journalLineSchema).min(2).max(50),
})
    .refine((v) => v.lines.reduce((s, l) => s + l.debitMinor, 0) ===
    v.lines.reduce((s, l) => s + l.creditMinor, 0), { message: 'Debits and credits must be equal', path: ['lines'] });
export const reverseJournalEntrySchema = z.object({ reason: text(300).min(1) });
export const listJournalQuerySchema = paginationQuerySchema.extend({
    from: isoDate.optional(),
    to: isoDate.optional(),
    sourceType: z.string().max(30).optional(),
});
export const trialBalanceQuerySchema = z.object({ asOf: isoDate.optional() });
export const ledgerQuerySchema = paginationQuerySchema.extend({
    from: isoDate.optional(),
    to: isoDate.optional(),
});
export const collectionReportQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((v) => v.to >= v.from, { message: 'End date cannot be before start date', path: ['to'] })
    .refine((v) => Date.parse(v.to) - Date.parse(v.from) <= 366 * 86_400_000, {
    message: 'Range can be at most 366 days',
    path: ['to'],
});
export const duesReportQuerySchema = paginationQuerySchema.extend({
    academicYearId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
});
//# sourceMappingURL=fees.js.map